/**
 * The clinic's profile: the copy on its About page, as shown there and edited there by
 * its own administrator.
 *
 * Named for the profile rather than for the page, because the page is only what it feeds
 * today. The branding a clinic will want next -- a logo, a palette -- belongs on the same
 * record and is meant to arrive through the same two calls.
 *
 * Four blocks of text plus two lists of cards, which is exactly what the page draws:
 *
 *   title + subtitle
 *   mission        (heading + paragraph)
 *   features       ("Ce oferim"       -- cards with an icon)
 *   services       ("Serviciile noastre" -- cards without one)
 *   call to action (heading + subtitle + a contact line)
 *
 * Every field may be blank, and blank means the clinic has not written that block: the
 * page leaves it out rather than drawing an empty heading. That is why nothing here is
 * required except a card's own Romanian title and text -- a card the clinic chose to keep
 * has to say something.
 *
 * Every label carries both languages. Romanian is what the clinic must write; English is
 * optional and falls back to Romanian, so a clinic that never translates still reads
 * correctly under either flag.
 */

import { newKey } from './localisedText';

/**
 * What a new feature card starts with. Lives here rather than in the About page's
 * `profileIcons` so the model does not depend on a module that returns JSX; that module
 * imports this one and is where the two are held to naming the same icon.
 */
export const DEFAULT_CARD_ICON: string = 'HEALING';

/** What the API returns. */
export interface ITenantProfile {
    aboutTitleRo?: string | null;
    aboutTitleEn?: string | null;
    aboutSubtitleRo?: string | null;
    aboutSubtitleEn?: string | null;
    missionTitleRo?: string | null;
    missionTitleEn?: string | null;
    missionBodyRo?: string | null;
    missionBodyEn?: string | null;
    featuresTitleRo?: string | null;
    featuresTitleEn?: string | null;
    servicesTitleRo?: string | null;
    servicesTitleEn?: string | null;
    ctaTitleRo?: string | null;
    ctaTitleEn?: string | null;
    ctaSubtitleRo?: string | null;
    ctaSubtitleEn?: string | null;
    ctaContactRo?: string | null;
    ctaContactEn?: string | null;
    features: ITenantProfileCard[];
    services: ITenantProfileCard[];
}

export interface ITenantProfileCard {
    /** The server's. Here for the page to key its list on, nothing more. */
    id: number;
    /** A name from the curated set in the About page's `profileIcons`. Never on a service card. */
    icon?: string | null;
    titleRo: string;
    titleEn?: string | null;
    bodyRo: string;
    bodyEn?: string | null;
}

/**
 * The draft the editor holds.
 *
 * Cards carry a `key` and no `id`. That is the one real difference from the service
 * catalogue's draft, and it follows from the save: the API replaces a clinic's cards
 * rather than reconciling them, because nothing anywhere points at a card the way a
 * therapist's disciplines point at a catalogue type. An id would be a number the editor
 * carried around and the server ignored.
 *
 * Nulls arrive as empty strings, because that is what a controlled text input holds.
 */
export interface ProfileDraft {
    aboutTitleRo: string;
    aboutTitleEn: string;
    aboutSubtitleRo: string;
    aboutSubtitleEn: string;
    missionTitleRo: string;
    missionTitleEn: string;
    missionBodyRo: string;
    missionBodyEn: string;
    featuresTitleRo: string;
    featuresTitleEn: string;
    servicesTitleRo: string;
    servicesTitleEn: string;
    ctaTitleRo: string;
    ctaTitleEn: string;
    ctaSubtitleRo: string;
    ctaSubtitleEn: string;
    ctaContactRo: string;
    ctaContactEn: string;
    features: ProfileCardDraft[];
    services: ProfileCardDraft[];
}

export interface ProfileCardDraft {
    key: string;
    icon: string;
    titleRo: string;
    titleEn: string;
    bodyRo: string;
    bodyEn: string;
}

/** What PUT /tenant-profile takes: the same shape, trimmed, with the editor's `key` dropped. */
export interface TenantProfileRequest {
    aboutTitleRo: string;
    aboutTitleEn: string;
    aboutSubtitleRo: string;
    aboutSubtitleEn: string;
    missionTitleRo: string;
    missionTitleEn: string;
    missionBodyRo: string;
    missionBodyEn: string;
    featuresTitleRo: string;
    featuresTitleEn: string;
    servicesTitleRo: string;
    servicesTitleEn: string;
    ctaTitleRo: string;
    ctaTitleEn: string;
    ctaSubtitleRo: string;
    ctaSubtitleEn: string;
    ctaContactRo: string;
    ctaContactEn: string;
    features: ProfileCardRequest[];
    services: ProfileCardRequest[];
}

export interface ProfileCardRequest {
    icon: string;
    titleRo: string;
    titleEn: string;
    bodyRo: string;
    bodyEn: string;
}

/**
 * A new feature card starts with an icon already chosen rather than none, so the tile it
 * draws is never an empty square the admin has to notice and go fix.
 */
export const emptyFeatureCard = (): ProfileCardDraft => ({
    key: newKey(),
    icon: DEFAULT_CARD_ICON,
    titleRo: '',
    titleEn: '',
    bodyRo: '',
    bodyEn: '',
});

/** The service group has never drawn an icon, so its cards do not carry one. */
export const emptyServiceCard = (): ProfileCardDraft => ({
    key: newKey(),
    icon: '',
    titleRo: '',
    titleEn: '',
    bodyRo: '',
    bodyEn: '',
});

/** Opens the editor on what is currently published. */
export const toProfileDraft = (profile: ITenantProfile | undefined): ProfileDraft => ({
    aboutTitleRo: profile?.aboutTitleRo ?? '',
    aboutTitleEn: profile?.aboutTitleEn ?? '',
    aboutSubtitleRo: profile?.aboutSubtitleRo ?? '',
    aboutSubtitleEn: profile?.aboutSubtitleEn ?? '',
    missionTitleRo: profile?.missionTitleRo ?? '',
    missionTitleEn: profile?.missionTitleEn ?? '',
    missionBodyRo: profile?.missionBodyRo ?? '',
    missionBodyEn: profile?.missionBodyEn ?? '',
    featuresTitleRo: profile?.featuresTitleRo ?? '',
    featuresTitleEn: profile?.featuresTitleEn ?? '',
    servicesTitleRo: profile?.servicesTitleRo ?? '',
    servicesTitleEn: profile?.servicesTitleEn ?? '',
    ctaTitleRo: profile?.ctaTitleRo ?? '',
    ctaTitleEn: profile?.ctaTitleEn ?? '',
    ctaSubtitleRo: profile?.ctaSubtitleRo ?? '',
    ctaSubtitleEn: profile?.ctaSubtitleEn ?? '',
    ctaContactRo: profile?.ctaContactRo ?? '',
    ctaContactEn: profile?.ctaContactEn ?? '',
    features: (profile?.features ?? []).map(toCardDraft),
    services: (profile?.services ?? []).map(toCardDraft),
});

const toCardDraft = (card: ITenantProfileCard): ProfileCardDraft => ({
    key: newKey(),
    icon: card.icon ?? '',
    titleRo: card.titleRo,
    titleEn: card.titleEn ?? '',
    bodyRo: card.bodyRo,
    bodyEn: card.bodyEn ?? '',
});

export const toTenantProfileRequest = (draft: ProfileDraft): TenantProfileRequest => ({
    aboutTitleRo: draft.aboutTitleRo.trim(),
    aboutTitleEn: draft.aboutTitleEn.trim(),
    aboutSubtitleRo: draft.aboutSubtitleRo.trim(),
    aboutSubtitleEn: draft.aboutSubtitleEn.trim(),
    missionTitleRo: draft.missionTitleRo.trim(),
    missionTitleEn: draft.missionTitleEn.trim(),
    missionBodyRo: draft.missionBodyRo.trim(),
    missionBodyEn: draft.missionBodyEn.trim(),
    featuresTitleRo: draft.featuresTitleRo.trim(),
    featuresTitleEn: draft.featuresTitleEn.trim(),
    servicesTitleRo: draft.servicesTitleRo.trim(),
    servicesTitleEn: draft.servicesTitleEn.trim(),
    ctaTitleRo: draft.ctaTitleRo.trim(),
    ctaTitleEn: draft.ctaTitleEn.trim(),
    ctaSubtitleRo: draft.ctaSubtitleRo.trim(),
    ctaSubtitleEn: draft.ctaSubtitleEn.trim(),
    ctaContactRo: draft.ctaContactRo.trim(),
    ctaContactEn: draft.ctaContactEn.trim(),
    features: draft.features.map(toCardRequest),
    services: draft.services.map(toCardRequest),
});

const toCardRequest = (card: ProfileCardDraft): ProfileCardRequest => ({
    icon: card.icon.trim(),
    titleRo: card.titleRo.trim(),
    titleEn: card.titleEn.trim(),
    bodyRo: card.bodyRo.trim(),
    bodyEn: card.bodyEn.trim(),
});

/**
 * The keys of every card the API would reject, so the editor can mark the field before
 * the round trip. Deliberately the same rules the server enforces -- a Romanian title and
 * a Romanian body on every card -- rather than a stricter set that would refuse something
 * the API would have accepted.
 *
 * Only cards appear here. Every other field on the page is genuinely optional: a clinic
 * that leaves its mission or its contact line blank has published a shorter page, not an
 * invalid one.
 *
 * Returns keys, not messages: which card is wrong is enough for the editor to decide what
 * to highlight, and the wording belongs to the translation file.
 */
export const invalidCardKeys = (draft: ProfileDraft): Set<string> => {
    const invalid: Set<string> = new Set<string>();

    [...draft.features, ...draft.services].forEach(card => {
        if (card.titleRo.trim().length === 0 || card.bodyRo.trim().length === 0) {
            invalid.add(card.key);
        }
    });

    return invalid;
};
